import React, {useMemo} from "react";
import {Container} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import {createColumnHelper} from "@tanstack/react-table";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import DataGrid from "../../Components/DataGrid/DataGrid.jsx";
import {bereichLabel, formatDateTimeDE} from "../../Components/Nutzung/format.js";
import {BereichLink, MitarbeiterLink, SeiteLink} from "../../Components/Nutzung/Links.jsx";

const ereignisSpalte = createColumnHelper();

/** Zeitdauern eines Ereignisses sind Millisekunden - unter einer Sekunde
 * als ms, sonst mit einer Nachkommastelle als Sekunden. Nur hier gebraucht,
 * deshalb lokal statt in Components/Nutzung/format.js (Teil 1, unangetastet). */
function formatDauerMs(ms) {
    if (ms === null || ms === undefined) {
        return "–";
    }
    const zahl = Number(ms);
    if (Number.isNaN(zahl)) {
        return "–";
    }
    return zahl < 1000 ? `${Math.round(zahl)} ms` : `${(zahl / 1000).toFixed(1)} s`;
}

const ART_VARIANTE = {
    Fehler: "danger",
    "Anmeldung fehlgeschlagen": "danger",
    Anmeldung: "success",
    Abmeldung: "secondary",
    Export: "info",
    Aktion: "primary",
};

/**
 * Rohdaten-Ereignisliste als DataGrid (Spezifikation Abschnitt 9,
 * /nutzung/daten/ereignisse - serverseitig paginiert, unverändert). Der
 * Reiter "Datenänderungen" entfällt hier (Auftrag Teil 8) - er ist jetzt
 * die eigenständige Seite Aenderungen.jsx.
 *
 * Der gemeinsame Zeitraum-/Mitarbeiter-/Bereichsfilter läuft bewusst NICHT
 * über die NutzungLayout-Filterleiste, sondern über DataGrids eigenen
 * additionalFilters-Mechanismus (Vorbild: Stock/ChangeLog.jsx) - der
 * Server-Endpunkt liest ausschließlich die Top-Level-Parameter
 * von/bis/user_id/bereich (kein "search", keine Spaltenfilter), deshalb
 * bleiben Sortierung und Spaltenfilter der Tabelle bewusst abgeschaltet:
 * eine Sortiermöglichkeit anzuzeigen, die der Server ignoriert, wäre
 * irreführend. NutzungLayout wird deshalb ohne eigene Filterleiste
 * eingebunden (nutzungFilters bleibt ungenutzt außer für das Menü).
 */
export default function NutzungEreignisse() {
    const {nutzer, bereiche} = usePage().props;

    const mitarbeiterOptionen = useMemo(
        () => [{value: "", label: "Alle Mitarbeiter"}, ...(nutzer || []).map((u) => ({value: String(u.id), label: u.name}))],
        [nutzer]
    );
    const bereichOptionen = useMemo(
        () => [{value: "", label: "Alle Bereiche"}, ...(bereiche || []).map((b) => ({value: b, label: bereichLabel(b)}))],
        [bereiche]
    );

    const zeitraumFilter = useMemo(
        () => [
            {key: "von", type: "date", paramKey: "von", label: "Von", defaultValue: ""},
            {key: "bis", type: "date", paramKey: "bis", label: "Bis", defaultValue: ""},
            {key: "user_id", type: "select", paramKey: "user_id", label: "Mitarbeiter", options: mitarbeiterOptionen, defaultValue: ""},
            {key: "bereich", type: "select", paramKey: "bereich", label: "Bereich", options: bereichOptionen, defaultValue: ""},
        ],
        [mitarbeiterOptionen, bereichOptionen]
    );

    const ereignisSpalten = useMemo(
        () => [
            ereignisSpalte.accessor("zeit", {
                id: "zeit",
                header: "Zeitpunkt",
                enableSorting: false,
                cell: (info) => formatDateTimeDE(info.getValue()),
                meta: {width: 150, help: "Zeitpunkt des Ereignisses (Europe/Berlin)."},
            }),
            ereignisSpalte.accessor("nutzer", {
                id: "nutzer",
                header: "Mitarbeiter",
                enableSorting: false,
                cell: (info) => <MitarbeiterLink id={info.row.original.user_id} name={info.getValue()} />,
                meta: {width: 140, cardTitle: true},
            }),
            ereignisSpalte.accessor("art", {
                id: "art",
                header: "Art",
                enableSorting: false,
                cell: (info) => (
                    <span className={`badge bg-${ART_VARIANTE[info.getValue()] || "body-secondary"} ${ART_VARIANTE[info.getValue()] ? "" : "text-body border"}`}>
                        {info.getValue()}
                    </span>
                ),
                meta: {width: 150, help: "Ereignisart - siehe Spezifikation Abschnitt 4 (kind)."},
            }),
            ereignisSpalte.accessor("bereich", {
                id: "bereich",
                header: "Bereich",
                enableSorting: false,
                cell: (info) => <BereichLink bereich={info.getValue()} />,
                meta: {width: 130},
            }),
            ereignisSpalte.accessor("bezeichnung", {
                id: "bezeichnung",
                header: "Bezeichnung",
                enableSorting: false,
                cell: (info) =>
                    info.row.original.route ? (
                        <SeiteLink route={info.row.original.route} />
                    ) : (
                        info.getValue() || <span className="text-muted">–</span>
                    ),
                meta: {width: 200},
            }),
            ereignisSpalte.accessor("methode", {
                id: "methode",
                header: "Methode",
                enableSorting: false,
                cell: (info) => info.getValue() && <span className="badge bg-body-secondary text-body border">{info.getValue()}</span>,
                meta: {width: 90},
            }),
            ereignisSpalte.accessor("status", {
                id: "status",
                header: "Status",
                enableSorting: false,
                cell: (info) => {
                    const wert = info.getValue();
                    if (wert === null || wert === undefined) {
                        return <span className="text-muted">–</span>;
                    }
                    const klasse = wert >= 500 ? "text-danger fw-semibold" : wert >= 400 ? "text-warning-emphasis fw-semibold" : "";
                    return <span className={klasse}>{wert}</span>;
                },
                meta: {width: 80},
            }),
            ereignisSpalte.accessor("dauer_ms", {
                id: "dauer_ms",
                header: "Dauer",
                enableSorting: false,
                cell: (info) => formatDauerMs(info.getValue()),
                meta: {width: 90},
            }),
            ereignisSpalte.accessor("ip", {
                id: "ip",
                header: "IP",
                enableSorting: false,
                cell: (info) => info.getValue() || <span className="text-muted">–</span>,
                meta: {width: 120},
            }),
        ],
        []
    );

    // Die Kopfleiste von NutzungLayout gehört zum gemeinsamen Rahmen (Teil 8:
    // "nur in den neuen Rahmen setzen") und bekommt hier ganz normal den
    // echten Filter-Hook - ihr Von/Bis dient als schnelle Orientierung, die
    // eigentliche Filterung dieser Tabelle bleibt bewusst bei DataGrids
    // eigenem Filter-Popover (additionalFilters oben), unverändert wie
    // zuvor - siehe Klassenkommentar.
    const nutzungFilters = useNutzungFilter();

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout gruppe="Verwaltung" titel="Rohdaten" nutzungFilters={nutzungFilters} showMitarbeiter={false} showBereich={false}>
                <p className="text-muted">
                    Jedes einzelne erfasste Ereignis im gewählten Zeitraum - Zeitraum, Mitarbeiter und
                    Bereich lassen sich über den Filter-Knopf der Tabelle eingrenzen.
                </p>
                <DataGrid
                    columns={ereignisSpalten}
                    getRowId={(row) => row.id}
                    initialSorting={[{id: "zeit", desc: true}]}
                    emptyLabel="Keine Ereignisse im gewählten Zeitraum."
                    itemLabel="Ereignisse"
                    itemLabelDative="Ereignissen"
                    dataUrl="/nutzung/daten/ereignisse"
                    additionalFilters={zeitraumFilter}
                    defaultPageSize={50}
                />
            </NutzungLayout>
        </Container>
    );
}
